// Bandeau de consentement aux traceurs (RGPD / recommandation CNIL 2020-092).
//
// Le markup vit dans Views/Shared/_CookieConsent.cshtml (contenu editorial en
// Razor) ; ce module ne fait que le piloter :
//   1. affiche le bandeau tant qu'aucun choix n'a ete enregistre ;
//   2. applique le choix : activation de Google Tag Manager, deblocage des
//      contenus tiers (iframes YouTube) ;
//   3. reouvre les preferences depuis le lien permanent du pied de page
//      (retrait du consentement aussi simple que son octroi).
//
// Aucun traceur non essentiel ne part avant l'appel a applyConsent().

import {
    ConsentCategory,
    ConsentState,
    OnConsentChanged,
    acceptAll,
    consentCategories,
    defaultChoices,
    hasConsent,
    isChoiceRequired,
    readConsent,
    rejectAll,
    saveConsent,
} from "../Helper/ConsentHelper";

/** Attribut porte par les iframes tierces mises en attente de consentement. */
const consentSrcAttribute = "data-consent-src";

declare global {
    interface Window {
        /** Identifiant du conteneur GTM, injecte par le serveur en production seulement. */
        __GTM_ID__?: string;
        dataLayer?: unknown[];
        /** Pose par le _Layout en meme temps que le Consent Mode par defaut. */
        gtag?: (...args: unknown[]) => void;
        /** Point d'entree public : ouvre la modale de gestion des cookies. */
        openCookieSettings?: () => void;
    }
}

// --- Activation des traceurs ------------------------------------------------

let gtmLoaded = false;

/**
 * Charge le conteneur GTM. Appele uniquement apres acceptation de la finalite
 * "analytics" : le script n'est jamais present dans la page avant le choix.
 */
function loadGoogleTagManager(): void {
    const id = window.__GTM_ID__;

    if (gtmLoaded || !id) return;
    gtmLoaded = true;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
}

/**
 * Charge les iframes tierces (YouTube) mises en attente et retire leur
 * vignette de remplacement.
 */
function releaseEmbeds(): void {
    document.querySelectorAll<HTMLIFrameElement>(`iframe[${consentSrcAttribute}]`).forEach((frame) => {
        const src = frame.getAttribute(consentSrcAttribute);

        if (!src) return;

        frame.removeAttribute(consentSrcAttribute);
        frame.src = src;
        frame.hidden = false;
        frame.dispatchEvent(new CustomEvent("ev:embed-released", { bubbles: true }));
    });

    document.querySelectorAll<HTMLElement>("[data-consent-placeholder]").forEach((node) => node.remove());
}

/** Applique l'etat de consentement aux traceurs de la page. */
function applyConsent(state: ConsentState): void {
    // Consent Mode v2 : informe Google du choix, y compris en cas de refus.
    // window.gtag est pose par le _Layout, en production uniquement.
    window.gtag?.("consent", "update", {
        analytics_storage: state.choices.analytics ? "granted" : "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
    });

    if (state.choices.analytics) loadGoogleTagManager();
    if (state.choices.media) releaseEmbeds();

    // Les traceurs deja charges ne peuvent pas etre retires du DOM en cours de
    // session : un refus apres coup prend effet au prochain chargement.
}

// --- Vignettes de remplacement des contenus tiers ---------------------------

/**
 * Remplace chaque iframe bloquee par une vignette expliquant pourquoi le
 * contenu n'est pas affiche, avec un bouton de consentement specifique a cette
 * finalite (consentement granulaire admis par la CNIL).
 */
function renderEmbedPlaceholders(): void {
    document.querySelectorAll<HTMLIFrameElement>(`iframe[${consentSrcAttribute}]`).forEach((frame) => {
        if (frame.previousElementSibling?.hasAttribute("data-consent-placeholder")) return;

        frame.hidden = true;

        const placeholder = document.createElement("div");
        placeholder.setAttribute("data-consent-placeholder", "");
        placeholder.className = "consent-embed";
        placeholder.innerHTML = `
            <p class="consent-embed__text">
                Ce contenu est hébergé par YouTube (Google). Son affichage dépose des
                traceurs sur votre appareil et nécessite votre accord.
            </p>
            <div class="consent-embed__actions">
                <button type="button" class="consent-embed__btn" data-consent-allow-media>
                    Autoriser et afficher le contenu
                </button>
                <button type="button" class="consent-embed__link" data-cookie-action="customize">
                    Gérer mes préférences
                </button>
            </div>`;

        frame.parentElement?.insertBefore(placeholder, frame);
    });
}

// --- Pilotage du bandeau et de la modale ------------------------------------

class CookieConsent {
    private root: HTMLElement;
    private banner: HTMLElement | null;
    private modal: HTMLElement | null;
    private overlay: HTMLElement | null;
    private lastFocused: HTMLElement | null = null;
    private bannerResizeObserver: ResizeObserver | null = null;

    constructor(root: HTMLElement) {
        this.root = root;
        this.banner = root.querySelector("[data-cookie-banner]");
        this.modal = root.querySelector("[data-cookie-modal]");
        this.overlay = root.querySelector("[data-cookie-overlay]");

        this.bindActions();
        this.root.hidden = false;

        if (isChoiceRequired()) this.showBanner();
    }

    /** Delegation : un seul listener pour le bandeau, la modale et le pied de page. */
    private bindActions(): void {
        document.addEventListener("click", (event) => {
            const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(
                "[data-cookie-action], [data-consent-allow-media]",
            );

            if (!target) return;

            if (target.hasAttribute("data-consent-allow-media")) {
                event.preventDefault();
                const current = readConsent()?.choices ?? defaultChoices();
                saveConsent({ ...current, media: true });
                this.hideBanner();
                return;
            }

            event.preventDefault();

            switch (target.getAttribute("data-cookie-action")) {
                case "accept":
                    acceptAll();
                    this.closeModal();
                    this.hideBanner();
                    break;
                case "reject":
                    rejectAll();
                    this.closeModal();
                    this.hideBanner();
                    break;
                case "customize":
                    this.openModal();
                    break;
                case "save":
                    saveConsent(this.readSwitches());
                    this.closeModal();
                    this.hideBanner();
                    break;
                case "close":
                    this.closeModal();
                    break;
            }
        });

        document.addEventListener("keydown", (event) => {
            if (!this.modal || this.modal.hidden) return;

            // Escape ferme la modale sans rien enregistrer : le bandeau reste
            // affiche tant qu'aucun choix n'a ete fait.
            if (event.key === "Escape") this.closeModal();
            if (event.key === "Tab") this.trapFocus(event);
        });
    }

    /** Lit l'etat des interrupteurs de la modale. */
    private readSwitches(): Record<ConsentCategory, boolean> {
        const choices = defaultChoices();

        consentCategories.forEach((category) => {
            const input = this.modal?.querySelector<HTMLInputElement>(`input[data-consent-category="${category}"]`);
            choices[category] = input?.checked === true;
        });

        return choices;
    }

    /** Positionne les interrupteurs sur le choix enregistre (refus par defaut). */
    private syncSwitches(): void {
        const current = readConsent()?.choices ?? defaultChoices();

        consentCategories.forEach((category) => {
            const input = this.modal?.querySelector<HTMLInputElement>(`input[data-consent-category="${category}"]`);
            if (input) input.checked = current[category];
        });
    }

    private showBanner(): void {
        if (!this.banner) return;

        this.banner.hidden = false;
        document.body.classList.add("has-cookie-banner");

        // Le bandeau est en position fixed : on reserve exactement sa hauteur en
        // bas de page pour ne pas masquer le pied de page. La hauteur depend du
        // texte replie sur plusieurs lignes, donc on la mesure au lieu de la
        // coder en dur, et on la suit au redimensionnement.
        const trackHeight = () => {
            const height = this.banner?.getBoundingClientRect().height ?? 0;
            document.documentElement.style.setProperty("--cookie-banner-height", `${Math.ceil(height)}px`);
        };

        trackHeight();

        if (typeof ResizeObserver !== "undefined") {
            this.bannerResizeObserver = new ResizeObserver(trackHeight);
            this.bannerResizeObserver.observe(this.banner);
        }
    }

    private hideBanner(): void {
        if (!this.banner) return;

        this.banner.hidden = true;
        document.body.classList.remove("has-cookie-banner");
        this.bannerResizeObserver?.disconnect();
        document.documentElement.style.removeProperty("--cookie-banner-height");
    }

    public openModal(): void {
        if (!this.modal) return;

        this.lastFocused = document.activeElement as HTMLElement | null;
        this.syncSwitches();
        this.root.hidden = false;
        this.modal.hidden = false;
        if (this.overlay) this.overlay.hidden = false;
        document.body.classList.add("has-cookie-modal");

        this.focusables()[0]?.focus();
    }

    private closeModal(): void {
        if (!this.modal || this.modal.hidden) return;

        this.modal.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
        document.body.classList.remove("has-cookie-modal");
        this.lastFocused?.focus();
    }

    private focusables(): HTMLElement[] {
        if (!this.modal) return [];

        return Array.from(
            this.modal.querySelectorAll<HTMLElement>("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"),
        ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    }

    /** Maintient le focus clavier dans la modale tant qu'elle est ouverte. */
    private trapFocus(event: KeyboardEvent): void {
        const items = this.focusables();

        if (items.length === 0) return;

        const first = items[0];
        const last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }
}

// --- Amorcage ---------------------------------------------------------------

function start(): void {
    const root = document.getElementById("cookieConsent");

    if (!root) return;

    const consent = new CookieConsent(root);

    window.openCookieSettings = () => consent.openModal();

    // Rejoue le choix deja enregistre a chaque chargement de page, puis reagit
    // aux changements faits pendant la session.
    OnConsentChanged.Bind((state: ConsentState) => applyConsent(state), "cookieConsent");

    const stored = readConsent();
    if (stored) applyConsent(stored);

    if (!hasConsent("media")) renderEmbedPlaceholders();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
} else {
    start();
}
